import { Controller } from "@hotwired/stimulus"

// Publishes that this tab is looking at the open thread, and hides the viewer's own
// chip from the header strip. Heartbeats stay on HTTP; the live list arrives on the
// conversation Turbo stream that the frame already subscribes to.
//
// Leave is delayed, not immediate, because a Turbo morph or frame refresh disconnects
// this controller and reconnects it on the same thread. An immediate DELETE would drop
// the viewer for everyone else in that gap. A module-level map cancels the pending
// leave when the remount heartbeats the same client id.
const CLIENT_PREFIX = "frontier.threadPresence.v1"
const HEARTBEAT_MS = 15_000
const LEAVE_GRACE_MS = 800
const BLUR_GRACE_MS = 4_000

const pendingLeaves = new Map()

function leaveKey(conversationId, clientId) {
  return `${conversationId}:${clientId}`
}

function cancelPendingLeave(conversationId, clientId) {
  const key = leaveKey(conversationId, clientId)
  const handle = pendingLeaves.get(key)
  if (!handle) return

  window.clearTimeout(handle)
  pendingLeaves.delete(key)
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}

function sendLeave({ url, clientId }) {
  const target = `${url}?client_id=${encodeURIComponent(clientId)}`
  fetch(target, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    keepalive: true
  }).catch(() => {})
}

function schedulePendingLeave({ conversationId, clientId, url, delay }) {
  cancelPendingLeave(conversationId, clientId)
  const key = leaveKey(conversationId, clientId)
  pendingLeaves.set(key, window.setTimeout(() => {
    pendingLeaves.delete(key)
    sendLeave({ url, clientId })
  }, delay))
}

export default class extends Controller {
  static targets = [ "list" ]
  static values = {
    conversationId: Number,
    userId: Number,
    url: String
  }

  connect() {
    this.clientId = this.readClientId()
    this.observer = new MutationObserver(() => this.hideSelf())
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.hideSelf()
    this.handleVisibility = () => this.visibilityChanged()
    this.handlePageHide = () => this.pageHide()
    document.addEventListener("visibilitychange", this.handleVisibility)
    window.addEventListener("pagehide", this.handlePageHide)
    this.becomePresent()
  }

  disconnect() {
    this.observer?.disconnect()
    document.removeEventListener("visibilitychange", this.handleVisibility)
    window.removeEventListener("pagehide", this.handlePageHide)
    this.stopHeartbeat()
    schedulePendingLeave({
      conversationId: this.conversationIdValue,
      clientId: this.clientId,
      url: this.urlValue,
      delay: LEAVE_GRACE_MS
    })
  }

  visibilityChanged() {
    if (document.visibilityState === "visible") {
      this.becomePresent()
      return
    }

    schedulePendingLeave({
      conversationId: this.conversationIdValue,
      clientId: this.clientId,
      url: this.urlValue,
      delay: BLUR_GRACE_MS
    })
    this.stopHeartbeat()
  }

  // pagehide is the reliable close-tab / full navigation signal. Disconnect still
  // schedules a leave as a retry — keepalive can lose the race with unload, and a
  // second DELETE of the same tab is a no-op.
  pageHide() {
    this.stopHeartbeat()
    sendLeave({ url: this.urlValue, clientId: this.clientId })
  }

  becomePresent() {
    cancelPendingLeave(this.conversationIdValue, this.clientId)
    this.heartbeat()
    this.startHeartbeat()
  }

  async heartbeat() {
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify({ client_id: this.clientId })
      })
      if (response.ok) this.element.dataset.threadPresenceReady = "true"
    } catch {
      // The interval retries; a dropped beat must not surface as a page error.
    }
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = window.setInterval(() => {
      if (document.visibilityState === "visible") this.heartbeat()
    }, HEARTBEAT_MS)
  }

  stopHeartbeat() {
    if (!this.heartbeatTimer) return

    window.clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
  }

  hideSelf() {
    if (!this.hasListTarget) return

    const userId = String(this.userIdValue)
    const others = []
    this.listTarget.querySelectorAll("[data-user-id]").forEach((person) => {
      const isSelf = person.dataset.userId === userId
      person.hidden = isSelf
      if (!isSelf) others.push(person)
    })
    this.listTarget.hidden = others.length === 0

    const names = others.map((person) => person.dataset.userName).filter(Boolean)
    if (names.length) {
      this.listTarget.setAttribute("aria-label", `Also looking: ${this.toSentence(names)}`)
    } else {
      this.listTarget.removeAttribute("aria-label")
    }
  }

  readClientId() {
    const key = `${CLIENT_PREFIX}.${this.userIdValue}.${this.conversationIdValue}`
    const existing = window.sessionStorage.getItem(key)
    if (existing) return existing

    const clientId = window.crypto.randomUUID()
    window.sessionStorage.setItem(key, clientId)
    return clientId
  }

  toSentence(names) {
    if (names.length <= 1) return names[0] || ""
    if (names.length === 2) return `${names[0]} and ${names[1]}`
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
  }
}
